// src/pages/AdminAgendaCitas/components/AppointmentForm.tsx
import React, { useCallback, useEffect, useState } from 'react';
import './../AdminAgendaCitas.scss';
import { Popup, Button, TextBox, LoadPanel, SelectBox } from 'devextreme-react';
import { useApiAppointments } from '../../../api/api.appoiments';
import Form, { SimpleItem, GroupItem } from 'devextreme-react/form';
import ScrollView from 'devextreme-react/scroll-view';
import { AppointmentFormData } from '../../../types/types.agendaCitas';
import { SysI_Agenda, EstadoCita } from '../../../types/types.agendaCitas';
import { useApiPatient } from '../../../api/api.patient';
import { TypeTableFilter } from '../../../types/types.system';
import { useAppSelector, useAppDispatch } from '../../../contexts/hooks';
import { newToast } from '../../../components';
import { clearPatient } from '../../../contexts/slices/appointments';
import { store } from '../../../contexts/store';
import { parseTimeString } from '../Utils/parseTime';
import { useScreenSize } from '../../../utils/media-query';
import ModalCreatePreOrder from '../../Preorders/Modals/ModalCreatePreOrder';
import { setMode } from '../../../contexts/slices/preorders';
import { setMode as setModeQuotation } from '../../../contexts/slices/quotations';
import ModalCreateQuotation from '../../billing/Quotations/Modals/ModalCreateQuotation';
import { viewPatient } from '../../../images/icons';
import { setModalPatient, setModePatient } from '../../../contexts/slices/orders';

// Extender la interfaz AppointmentFormData para incluir los nuevos campos
interface ExtendedAppointmentFormData extends AppointmentFormData {
  patientDocument: string;
  patientName: string;
  patientLastName: string;
  patientGender: number | null;
  patientBirthDate: Date | null;
}

interface AppointmentFormProps {
  visible: boolean;
  formData: ExtendedAppointmentFormData;
  setFormData: React.Dispatch<React.SetStateAction<ExtendedAppointmentFormData>>;
  isEditMode: boolean;
  isSearchingPatient: boolean;
  setIsSearchingPatient: React.Dispatch<React.SetStateAction<boolean>>;
  patientFound: boolean;
  setPatientFound: React.Dispatch<React.SetStateAction<boolean>>;
  agendas: SysI_Agenda[];
  loadingForm?: boolean;
  loadingPatientSearch?: boolean;
  onSave: (formData: ExtendedAppointmentFormData) => void;
  onCancel: () => void;
  initialPatientDocument?: string;
  quotationId?: number | null;
  preorderId?: number | null;
  onAppointmentUpdated?: (appointmentId: number, updates: Partial<ExtendedAppointmentFormData>) => void;
}

const AppointmentForm: React.FC<AppointmentFormProps> = ({ visible, formData, setFormData, isEditMode, isSearchingPatient, setIsSearchingPatient, patientFound, setPatientFound, agendas, loadingForm = false, loadingPatientSearch = false, onSave, onCancel, initialPatientDocument, quotationId, preorderId, onAppointmentUpdated }) => {
  // Obtener permisos y API para búsqueda de pacientes
  const { findUser } = useApiPatient();
  const Prm_Pac = useAppSelector((x) => x.userInfo.Permissions?.Prm_Pac);
  const [modalPreOrderVisible, setModalPreOrderVisible] = useState(false);
  const [modalQuotationVisible, setModalQuotationVisible] = useState(false);
  const [currentIdPreOrder, setCurrentIdPreOrder] = useState<number | undefined>(undefined);
  const Prm_Preordenes = useAppSelector((x) => x.userInfo.Permissions?.Prm_Preordenes);
  const Prm_Cotizaciones = useAppSelector((x) => x.userInfo.Permissions?.Prm_Cotizaciones);

  // Añadir el dispatch
  const dispatch = useAppDispatch();
  const { isSmall, isXSmall, isMedium } = useScreenSize();

  // Array para selector de género
  const genderOptions = [
    { value: 1, text: 'Femenino' },
    { value: 2, text: 'Masculino' },
  ];

  const { updateCitaPreorden } = useApiAppointments();

  // Verificar si la cita está completada (estado = 2)
  const isAppointmentCompleted = isEditMode && formData.status === 2;

  // Efecto principal para manejar la inicialización del modal
  useEffect(() => {
    // Modo nuevo - solo configurar datos iniciales si no están ya configurados
    if (initialPatientDocument && initialPatientDocument.trim() !== '' && formData.patientDocument !== initialPatientDocument) {
      setFormData((prev) => ({
        ...prev,
        patientDocument: initialPatientDocument,
        quotationId: quotationId || null,
        preorderId: preorderId || null,
      }));

      // Solo buscar si no hemos encontrado ya el paciente
      if (!patientFound) {
        setTimeout(() => {
          handleSearchPatient(initialPatientDocument);
        }, 100);
      }
    } else if (!initialPatientDocument && !formData.patientDocument && visible) {
      // Sin documento inicial - solo limpiar si hay datos previos
      setFormData((prev) => ({
        ...prev,
        patientId: undefined,
        patientDocument: '',
        patientName: '',
        patientLastName: '',
        patientGender: null,
        patientBirthDate: null,
        quotationId: quotationId || null,
        preorderId: preorderId || null,
      }));
      setPatientFound(false);
      dispatch(clearPatient());
    }
  }, [visible, isEditMode, initialPatientDocument, quotationId, preorderId]); // Removido patientFound de dependencias

  // Efecto separado SOLO para limpiar al cerrar
  useEffect(() => {
    if (!visible) {
      setFormData((prev) => ({
        ...prev,
        patientId: undefined,
        patientDocument: '',
        patientName: '',
        patientLastName: '',
        patientGender: null,
        patientBirthDate: null,
      }));
      setPatientFound(false);
      dispatch(clearPatient());
    }
  }, [visible]); // Solo depende de visible

  // Manejar cambios en los campos del formulario
  const handleFormChange = (field: keyof ExtendedAppointmentFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Manejar cambio de documento del paciente y búsqueda automática
  const handlePatientDocumentChange = (e: any) => {
    const document = e.value;
    handleFormChange('patientDocument', document);

    // Si el documento cambia y ya habíamos encontrado un paciente, limpiamos los datos
    if (patientFound) {
      setPatientFound(false);
      setFormData((prev) => ({
        ...prev,
        patientId: undefined,
        patientName: '',
        patientLastName: '',
        patientGender: null,
        patientBirthDate: null,
      }));
      dispatch(clearPatient());
    }
  };

  // Función para manejar la tecla ENTER en el campo de documento
  const handlePatientDocumentKeyDown = (e: any) => {
    if (e.event.key === 'Enter') {
      e.event.preventDefault();
      e.event.stopPropagation();

      const document = formData.patientDocument?.trim();
      if (document && document.length > 0 && !isSearchingPatient) {
        handleSearchPatient(document);
      }
    }
  };

  // Manejar cambio de agenda y recalcular fecha de fin
  const handleFormAgendaChange = (e: any) => {
    const newAgendaId = e.value;
    const agenda = agendas.find((a) => a.Ag_Id === newAgendaId);

    if (agenda && agenda.Ag_PeriodoCita) {
      const newEndDate = new Date(formData.startDate);
      newEndDate.setMinutes(newEndDate.getMinutes() + agenda.Ag_PeriodoCita);
      setFormData((prev) => ({
        ...prev,
        agendaId: newAgendaId,
        endDate: newEndDate,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        agendaId: newAgendaId,
      }));
    }
  };

  // Manejar cambio de fecha de inicio
  const handleStartDateChange = (e: any) => {
    const newStartDate = e.value instanceof Date ? e.value : new Date(e.value);
    const now = new Date();

    if (isNaN(newStartDate.getTime())) {
      console.error('Fecha inválida:', e.value);
      return;
    }

    if (!isEditMode && newStartDate < now) {
      newToast('WARNING', 'No se puede programar una cita en una fecha y hora pasada', 3);
      return;
    }

    const localStartDate = new Date(newStartDate.getFullYear(), newStartDate.getMonth(), newStartDate.getDate(), newStartDate.getHours(), newStartDate.getMinutes());
    const newEndDate = new Date(newStartDate);
    const agendaId = formData.agendaId;
    const period = agendaId ? agendas.find((a) => a.Ag_Id === agendaId)?.Ag_PeriodoCita || 30 : 30;

    newEndDate.setMinutes(newEndDate.getMinutes() + period);
    setFormData((prev) => ({
      ...prev,
      startDate: localStartDate,
      endDate: newEndDate,
    }));
  };

  // Manejar cambio de fecha fin
  const handleEndDateChange = (e: any) => {
    try {
      if (isEditMode) {
        const newEndDate = e.value instanceof Date ? new Date(e.value) : new Date(e.value);
        setFormData((prev) => ({
          ...prev,
          endDate: newEndDate,
        }));
        return;
      }

      const newEndDate = e.value instanceof Date ? new Date(e.value) : new Date(e.value);
      const now = new Date();

      if (isNaN(newEndDate.getTime())) {
        console.error('Fecha inválida:', e.value);
        return;
      }

      if (newEndDate < now) {
        newToast('WARNING', 'La hora de fin no puede ser anterior a la hora actual', 3);
        return;
      }

      if (newEndDate < formData.startDate) {
        newToast('WARNING', 'La hora de fin no puede ser anterior a la hora de inicio', 3);
        return;
      }
      setFormData((prev) => ({
        ...prev,
        endDate: newEndDate,
      }));
    } catch (error) {
      console.error('Error en handleEndDateChange:', error);
    }
  };

  // Buscar paciente por documento
  const handleSearchPatient = async (manualDocumentOrEvent?: string | any) => {
    let document: string;

    if (typeof manualDocumentOrEvent === 'string') {
      document = manualDocumentOrEvent.trim();
    } else {
      document = formData.patientDocument.trim();
    }

    if (!document) {
      newToast('WARNING', 'Debe ingresar un documento', 3);
      return;
    }

    if (!Prm_Pac) {
      newToast('INFO', 'No tiene permisos para buscar pacientes', 4);
      return;
    }

    dispatch(clearPatient());
    setIsSearchingPatient(true);

    try {
      const documentToFind = document;

      await findUser([{ TypeFilter: TypeTableFilter.Equal, Field: 'Pc_Identificacion', Value: documentToFind }], setIsSearchingPatient, true, 'appointment');

      // Pequeña pausa para permitir que la UI se actualice
      await new Promise((resolve) => setTimeout(resolve, 100));

      const updatedPatientData = store.getState().globalAppointments?.PatientData;

      if (updatedPatientData && updatedPatientData.Pc_Id) {
        if (updatedPatientData.Pc_Identificacion !== documentToFind) {
          console.error('Error: El documento encontrado no coincide con el buscado', {
            buscado: documentToFind,
            encontrado: updatedPatientData.Pc_Identificacion,
          });
          newToast('ERROR', 'Error en la búsqueda: el documento encontrado no coincide con el buscado', 4);
          dispatch(clearPatient());
          setPatientFound(false);
          return;
        }

        setFormData((prev) => ({
          ...prev,
          patientId: updatedPatientData.Pc_Id,
          patientDocument: updatedPatientData.Pc_Identificacion || '',
          patientName: updatedPatientData.Pc_Nombre || '',
          patientLastName: updatedPatientData.Pc_Apellido1 || '',
          patientGender: updatedPatientData.Pc_Gn_Id || null,
          patientBirthDate: updatedPatientData.Pc_FechaNacimiento ? new Date(updatedPatientData.Pc_FechaNacimiento) : null,
        }));

        setPatientFound(true);
      } else {
        setFormData((prev) => ({
          ...prev,
          patientId: undefined,
          patientName: '',
          patientLastName: '',
          patientGender: null,
          patientBirthDate: null,
          patientDocument: document,
        }));

        setPatientFound(false);
      }
    } catch (error) {
      console.error('Error al buscar paciente:', error);
      newToast('ERROR', 'Error al buscar paciente', 3);
      dispatch(clearPatient());
      setPatientFound(false);
    } finally {
      setIsSearchingPatient(false);
    }
  };

  // Validar y guardar la cita
  const handleSaveAppointment = () => {
    const now = new Date();

    if (!formData.agendaId) {
      newToast('WARNING', 'Debe seleccionar un usuario', 3);
      return;
    }

    if (!formData.patientDocument) {
      newToast('WARNING', 'Debe ingresar el documento del paciente', 3);
      return;
    }

    if (!patientFound && (!formData.patientName || !formData.patientLastName)) {
      newToast('WARNING', 'Debe ingresar el nombre y apellido del paciente', 3);
      return;
    }

    if (!isEditMode && formData.startDate < now) {
      newToast('WARNING', 'No se puede programar una cita en una fecha y hora pasada', 3);
      return;
    }

    // Validar horarios de agenda
    const selectedAgenda = agendas.find((a) => a.Ag_Id === formData.agendaId);
    if (selectedAgenda && selectedAgenda.Ag_HoraInicio && selectedAgenda.Ag_HoraFin) {
      const agendaStartTime = parseTimeString(selectedAgenda.Ag_HoraInicio);
      const agendaEndTime = parseTimeString(selectedAgenda.Ag_HoraFin);

      const startHours = formData.startDate.getHours();
      const startMinutes = formData.startDate.getMinutes();
      const endHours = formData.endDate.getHours();
      const endMinutes = formData.endDate.getMinutes();

      const startTimeValue = startHours + startMinutes / 60;
      const endTimeValue = endHours + endMinutes / 60;
      const agendaStartValue = agendaStartTime.hour + agendaStartTime.minute / 60;
      const agendaEndValue = agendaEndTime.hour + agendaEndTime.minute / 60;

      if (!isEditMode && startTimeValue < agendaStartValue) {
        newToast('WARNING', `La agenda de ${selectedAgenda.Ag_Nombre} inicia a las: ${selectedAgenda.Ag_HoraInicio}`, 3);
        return;
      }

      if (!isEditMode && startTimeValue > agendaEndValue) {
        newToast('WARNING', `El usuario ${selectedAgenda.Ag_Nombre} termina agenda a las ${selectedAgenda.Ag_HoraFin}. Seleccione otra hora disponible`, 3);
        return;
      }

      if (!isEditMode && endTimeValue > agendaEndValue) {
        newToast('WARNING', `La hora de fin debe ser anterior a ${selectedAgenda.Ag_HoraFin}`, 3);
        return;
      }
    }

    const dataToSave = {
      ...formData,
      quotationId: formData.quotationId || quotationId,
      preorderId: formData.preorderId || preorderId,
    };

    onSave(dataToSave);
  };

  const handleStatusChange = (e: any) => {
    setFormData((prev) => {
      if (prev.status === e.value) {
        return prev;
      }
      return {
        ...prev,
        status: e.value,
      };
    });
  };

  const handlePreOrderCreated = async (newPreOrderId: number) => {
    try {
      // 1. Actualizar el estado local del formulario
      setFormData((prev) => ({
        ...prev,
        preorderId: newPreOrderId,
      }));

      if (formData.id) {
        // Actualizar la cita en la base de datos
        await updateCitaPreorden(formData.id, newPreOrderId);

        // NUEVA LÍNEA: Notificar al componente padre si tiene el callback
        if (onAppointmentUpdated) {
          onAppointmentUpdated(formData.id, { preorderId: newPreOrderId });
        }
      }

      setModalPreOrderVisible(false);
    } catch (error) {
      console.error('Error actualizando la cita con preorden:', error);
      newToast('ERROR', 'Error al vincular la preorden con la cita', 4);
    }
  };

  const handleOpenPreOrder = () => {
    if (formData.preorderId) {
      dispatch(setMode({ mode: 'view' }));
      setCurrentIdPreOrder(formData.preorderId);
    } else {      
      dispatch(setMode({ mode: 'new' }));
      setCurrentIdPreOrder(undefined);
    }
    setModalPreOrderVisible(true);
  };

  const handleOpenQuotation = () => {
    if (formData.quotationId) {
      dispatch(setModeQuotation({ mode: 'view' }));
      setModalQuotationVisible(true);
    }
  };

  const handleClosePreOrder = () => {
    setModalPreOrderVisible(false);
  };

  const handleCloseQuotation = () => {
    setModalQuotationVisible(false);
  };

  const handleClickViewPatient = useCallback(() => {
    dispatch(setModalPatient({ visible: true, context: 'appointment' }));
    findUser([{ TypeFilter: TypeTableFilter.Equal, Field: 'Pc_Identificacion', Value: formData.patientDocument }], () => {}, true, 'appointment');
  }, [formData, findUser]);

  return (
    <Popup
      showCloseButton={true}
      visible={visible}
      onHiding={onCancel}
      fullScreen={isXSmall} // Solo pantallas muy pequeñas
      dragEnabled={!isXSmall}
      hideOnOutsideClick={false}
      showTitle={true}
      title={isEditMode ? 'Editar Cita' : 'Nueva Cita'}
      width={isXSmall ? '100%' : isSmall ? '90%' : isMedium ? '80%' : '700px'}
      height='auto'
      maxWidth='800px'
      maxHeight='85vh'
      className='appointment-form-popup'
      position={isXSmall ? undefined : { my: 'center', at: 'center', of: window }}>
      {/* Panel de carga para la inicialización del formulario */}
      <LoadPanel visible={loadingForm} message='Preparando formulario...' showPane={true} shading={true} position={{ my: 'center', at: 'center', of: '.appointment-form-popup' }} />

      {/* Panel de carga específico para búsqueda de pacientes */}
      <LoadPanel visible={isSearchingPatient || loadingPatientSearch} message='Buscando paciente...' showPane={true} shading={true} position={{ my: 'center', at: 'center', of: '.appointment-form-popup' }} />

      <ScrollView width='100%' height='100%'>
        <div className='appointment-form'>
          {/* Mensaje informativo para citas completadas */}
          {isAppointmentCompleted && (
            <div
              style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: '4px',
                padding: '10px',
                marginBottom: '15px',
                color: '#856404',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
              <i className='dx-icon dx-icon-info' style={{ fontSize: '16px' }}></i>
              <span>Esta cita está completada. Solo se puede modificar el estado.</span>
            </div>
          )}

          <Form labelMode='floating'>
            <GroupItem></GroupItem>
            <GroupItem caption='' colCount={2}>
              {/* Selección de Agenda (usuario) - SIEMPRE editable */}
              <SimpleItem
                dataField='agendaId'
                editorType='dxSelectBox'
                label={{ text: 'Usuario' }}
                editorOptions={{
                  items: agendas,
                  displayExpr: 'Ag_Nombre',
                  valueExpr: 'Ag_Id',
                  value: formData.agendaId,
                  onValueChanged: handleFormAgendaChange,
                  searchEnabled: true,
                  disabled: isAppointmentCompleted,
                  placeholder: 'Seleccione un usuario',
                  showClearButton: true,
                }}
                isRequired={true}
              />
              {formData.quotationId && (
                <SimpleItem
                  dataField='quotationId'
                  editorType='dxTextBox'
                  label={{ text: 'Cotización No.' }}
                  editorOptions={{
                    value: String(formData.quotationId),
                    disabled: true,
                  }}
                />
              )}
              {formData.preorderId && (
                <SimpleItem
                  dataField='preorderId'
                  editorType='dxTextBox'
                  label={{ text: 'Preorden No.' }}
                  editorOptions={{
                    value: String(formData.preorderId),
                    disabled: true,
                  }}
                />
              )}
            </GroupItem>

            <GroupItem caption='' colCount={2}>
              {/* Fecha y hora de inicio */}
              <SimpleItem
                dataField='startDate'
                editorType='dxDateBox'
                label={{ text: 'Fecha y hora de inicio' }}
                editorOptions={{
                  type: 'datetime',
                  value: formData.startDate,
                  onValueChanged: handleStartDateChange,
                  pickerType: 'calendar',
                  useMaskBehavior: true,
                  displayFormat: 'dd/MM/yyyy HH:mm',
                  min: isEditMode ? undefined : new Date(),
                  disabled: isAppointmentCompleted,
                }}
                isRequired={true}
              />
              {/* Fecha y hora de fin */}
              <SimpleItem
                dataField='endDate'
                editorType='dxDateBox'
                label={{ text: 'Fecha y hora de fin' }}
                editorOptions={{
                  type: 'datetime',
                  value: formData.endDate,
                  onValueChanged: handleEndDateChange,
                  pickerType: 'calendar',
                  useMaskBehavior: true,
                  displayFormat: 'dd/MM/yyyy HH:mm',
                  min: isEditMode ? undefined : new Date(),
                  disabled: isAppointmentCompleted,
                }}
              />
              {/* Estado de la cita (solo en modo edición) */}
              {isEditMode && (
                <SimpleItem
                  render={() => (
                    <div className='status-select-container'>
                      <SelectBox
                        items={[
                          { value: EstadoCita.Programada, text: 'Programada' },
                          { value: EstadoCita.Completada, text: 'Completada' },
                          { value: EstadoCita.Cancelada, text: 'Cancelada' },
                          { value: EstadoCita.NoAsistio, text: 'No Asistió' },
                        ]}
                        value={formData.status}
                        onValueChanged={handleStatusChange}
                        valueExpr='value'
                        displayExpr='text'
                        label='Estado'
                        labelMode='floating'
                      />
                    </div>
                  )}
                />
              )}
            </GroupItem>

            <GroupItem colCount={2}>
              {/* Búsqueda de paciente por cédula */}
              <SimpleItem
                render={() => (
                  <TextBox
                    placeholder='Documento de identidad'
                    value={formData.patientDocument}
                    onValueChanged={handlePatientDocumentChange}
                    onEnterKey={handlePatientDocumentKeyDown}
                    disabled={isSearchingPatient || isAppointmentCompleted}
                    width='100%'
                    label='Documento del paciente (Presione ENTER para buscar)'
                    labelMode='floating'
                    buttons={[
                      {
                        name: 'viewPatient',
                        location: 'after',
                        options: {
                          icon: viewPatient,
                          stylingMode: 'text',
                          hint: 'Ver datos del paciente',
                          onClick: handleClickViewPatient,
                        },
                      },
                    ]}
                  />
                )}
              />
            </GroupItem>

            {/* Información del paciente */}
            <GroupItem colCount={2}>
              <SimpleItem
                dataField='patientName'
                editorType='dxTextBox'
                label={{ text: 'Nombre' }}
                editorOptions={{
                  value: formData.patientName,
                  readOnly: true, // SIEMPRE deshabilitado
                  placeholder: 'Nombre del paciente',
                }}
                isRequired={!patientFound}
              />

              <SimpleItem
                dataField='patientLastName'
                editorType='dxTextBox'
                label={{ text: 'Apellido' }}
                editorOptions={{
                  value: formData.patientLastName,
                  readOnly: true, // SIEMPRE deshabilitado
                  placeholder: 'Apellido del paciente',
                }}
                isRequired={!patientFound}
              />

              {/* Género */}
              <SimpleItem
                dataField='patientGender'
                editorType='dxSelectBox'
                label={{ text: 'Género' }}
                editorOptions={{
                  items: genderOptions,
                  displayExpr: 'text',
                  valueExpr: 'value',
                  value: formData.patientGender,
                  readOnly: true, // SIEMPRE deshabilitado
                  placeholder: 'Seleccione género',
                }}
              />

              {/* Fecha de nacimiento */}
              <SimpleItem
                dataField='patientBirthDate'
                editorType='dxDateBox'
                label={{ text: 'Fecha de Nacimiento' }}
                editorOptions={{
                  type: 'date',
                  value: formData.patientBirthDate,
                  readOnly: true, // SIEMPRE deshabilitado
                  displayFormat: 'dd/MM/yyyy',
                  max: new Date(),
                  placeholder: 'Fecha de nacimiento',
                }}
              />
            </GroupItem>

            {/* Notas */}
            <GroupItem caption='Notas'>
              <SimpleItem
                dataField='notes'
                editorType='dxTextArea'
                label={{ text: 'Notas' }}
                editorOptions={{
                  value: formData.notes,
                  onValueChanged: (e) => handleFormChange('notes', e.value),
                  height: 100,
                  placeholder: 'Información adicional sobre la cita',
                  disabled: isAppointmentCompleted,
                }}
              />
            </GroupItem>

            {/* Botones */}
            <GroupItem>
              <div className='form-action-buttons'>
                <div className='form-action-button'>
                  {formData.quotationId && quotationId == null && <Button text='Ver cotización' onClick={handleOpenQuotation} type='default' style={{ marginRight: '12px' }} />}
                  {preorderId == null && isEditMode && <Button text={formData.preorderId ? 'Ver preorden' : 'Crear preorden'} onClick={handleOpenPreOrder} type='default' />}
                </div>
                <div className='right-buttons'>
                  <Button text='Cancelar' onClick={onCancel} stylingMode='outlined' />
                  <Button text='Guardar' type='default' onClick={handleSaveAppointment} />
                </div>
              </div>
            </GroupItem>
          </Form>
        </div>
      </ScrollView>

      {/* Indicador de carga al buscar paciente */}
      <LoadPanel visible={isSearchingPatient} showIndicator={true} shading={true} showPane={true} message='Buscando paciente...' />

      {Prm_Preordenes  && <ModalCreatePreOrder visible={modalPreOrderVisible} idPreOrder={currentIdPreOrder} setVisible={handleClosePreOrder} setCurrentPreOrderCreated={handlePreOrderCreated} idPatient={formData.patientId} />}

      {Prm_Cotizaciones && formData.quotationId !== undefined && <ModalCreateQuotation visible={modalQuotationVisible} setVisible={handleCloseQuotation} idQuotation={formData.quotationId ? formData.quotationId : -1} />}
    </Popup>
  );
};

export default AppointmentForm;
